import React, { useEffect, useState, useRef } from 'react';
import { Share, X, ChevronLeft, Route, BookCheck } from 'lucide-react';
import Header from '../components/newHeader';
import TestCard from '../components/TestCard';
import FormRatingTrendChart from '../components/FormRatingTrendChart';
import DifficultyAnalysisPanel from '../components/DifficultyAnalysisPanel';
import logoAnalysis from '../assets/logo_analysis.png';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import FloatingNavButtons from '../components/FloatingNavButtons';
import FloatingButton from '../components/common/FloatingButton';
import logoKytest from '../assets/logo_kytest.png';
import ExamProblemList from '../components/ExamProblemList';

function MyResultPage() {
  const { code } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [analysis, setAnalysis] = useState(null);
  const [examInfo, setExamInfo] = useState(null);
  const [fieldInfo, setFieldInfo] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [examList, setExamList] = useState([]);
  const [currentExamIndex, setCurrentExamIndex] = useState(0);
  const [v3Data, setV3Data] = useState(null);
  const [testHistory, setTestHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('access_token'));
  const [rating, setRating] = useState(null);
  const [delta, setDelta] = useState(null);
  const [reportValid, setReportValid] = useState(false);
  const [resultMapData, setResultMapData] = useState(null);
  const [currentDimension, setCurrentDimension] = useState(4); // d1=1, d2=2, d3=3, d4=4
  const [showExamOnly, setShowExamOnly] = useState(false); // 출제문제만 보기 토글
  const [highlightedCell, setHighlightedCell] = useState(null); // 하이라이트할 셀 정보
  const problemListScrollRef = useRef(null);
  const tableScrollRef = useRef(null);

  // gradeProgress 계산을 상단에서 정의
  const gradeProgress = (() => {
    const targetGrade = analysis?.target_grade;
    const progress = analysis?.progress || 0;

    switch (targetGrade) {
      case 0: return 0.8 + 0.2 * progress;
      case 1: return 0.6 + 0.2 * progress;
      case 2: return 0.4 + 0.2 * progress;
      case 3: return 0.4 * progress;
      default: return progress;
    }
  })();

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    const name = localStorage.getItem('student_name');
    const email = localStorage.getItem('student_email');

    if (!token || !name || !email) {
      navigate('/', { replace: true });
      return;
    }

    setToken(token);

    let isMounted = true;

    const fetchData = async () => {
      try {
        
        // Fetch resultmap data
        if (token) {
          try {
            const resultMapResponse = await axios.get(
              `${process.env.REACT_APP_API_BASE_URL}/api/student/resultmap?exam_id=${code}`,
              {
                headers: { 'Authorization': `Bearer ${token}` }
              }
            );
          
          // Parse the response data - data is nested inside data property
          const responseData = resultMapResponse.data.data;
          setResultMapData(responseData); // Store entire response for dimension switching
            
          } catch (error) {
            console.error('Error fetching resultmap data:', error);
          }
        }
        
        if (isMounted) {

          // v3 API 데이터 캐싱 확인
          const CACHE_KEY = 'v3_test_data';
          const CACHE_DURATION = 5 * 60 * 1000; // 5분
          
          let v3Data = null;
          let allTests = [];
          
          // navigation state에서 forceRefresh 확인
          const forceRefresh = location.state?.forceRefresh || false;
          
          // 캐시된 데이터 확인 (forceRefresh가 아닌 경우에만)
          if (!forceRefresh) {
            const cachedData = sessionStorage.getItem(CACHE_KEY);
            if (cachedData) {
              const parsed = JSON.parse(cachedData);
              const now = Date.now();
              
              // 캐시가 유효한 경우 (5분 이내)
              if (parsed.timestamp && (now - parsed.timestamp) < CACHE_DURATION) {
                  v3Data = parsed.data;
              }
            }
          }
          
          // 캐시가 없거나 만료된 경우에만 API 호출
          if (!v3Data) {
            const v3Response = await axios.get(
              `${process.env.REACT_APP_API_BASE_URL}/api/student/home/v3`,
              {
                headers: { 'Authorization': `Bearer ${token}` }
              }
            );
            
            if (v3Response.data) {
              v3Data = v3Response.data;
              // 캐시에 저장
              sessionStorage.setItem(CACHE_KEY, JSON.stringify({
                data: v3Data,
                timestamp: Date.now()
              }));
            }
          }

          if (v3Data && isMounted) {
            // 모든 테스트 기록 추출
            const stamps = v3Data.data?.stamps || [];
            
            // 현재 exam 찾기 위한 변수
            let currentExam = null;
            let currentStamp = null;
            
            for (const stamp of stamps) {
              for (const exam of stamp.exams || []) {
                // 정답/오답 계산
                let correctCount = 0;
                let totalQuestions = 0;
                
                if (exam.by_step) {
                  for (let level = 1; level <= 5; level++) {
                    const stepData = exam.by_step[String(level)];
                    if (Array.isArray(stepData) && stepData.length >= 2) {
                      const [totalProblems, correct] = stepData;
                      totalQuestions += totalProblems || 0;
                      correctCount += correct || 0;
                    }
                  }
                } else if (exam.length) {
                  totalQuestions = exam.length;
                }
                
                const wrongCount = totalQuestions > 0 ? totalQuestions - correctCount : 0;
                
                const testData = {
                  ...exam,
                  stamp_id: stamp.stamp_id,
                  tree_name: stamp.tree_name,
                  edu_ver: stamp.edu_ver,
                  correct_count: correctCount,
                  wrong_count: wrongCount
                };
                
                allTests.push(testData);
                
                // 현재 exam_id와 일치하는 테스트 찾기
                if (exam.exam_id === parseInt(code)) {
                  currentExam = testData;
                  currentStamp = stamp;
                }
              }
            }
            
            // 날짜순으로 정렬
            allTests.sort((a, b) => {
              const dateA = new Date(a.updated_at || a.start_time || 0).getTime();
              const dateB = new Date(b.updated_at || b.start_time || 0).getTime();
              return dateB - dateA;
            });
            
            setTestHistory(allTests);
            
            // 현재 exam 데이터로 상태 설정
            if (currentExam) {
              // v3 데이터에서 필요한 정보 추출 및 설정
              setAnalysis(currentExam.analysis || null);
              setFieldInfo({
                tree_name: currentExam.tree_name || '',
                edu_ver: currentExam.edu_ver || ''
              });
              setRating(currentExam.rating || null);
              setDelta(currentExam.delta || null);
              setReportValid(currentExam.report_info?.is_closed === true || currentExam.report_valid || false);
              
              setExamInfo({
                ...currentExam,
                exam_type: currentExam.exam_type || '',
                updated_at: currentExam.updated_at || new Date().toISOString(),
                exam_count: currentExam.exam_count || 0,
                difficulty: currentExam.difficulty || '',
                length: currentExam.length || 0,
                total_record: currentExam.total_record || [],
                record_acc: currentExam.record_acc || [],
                accuracy: currentExam.accuracy || []
              });
            } else {
              // v3에 현재 exam이 없는 경우 기본값 설정
              console.warn(`Exam with id ${code} not found in v3 data`);
              setExamInfo({
                exam_id: parseInt(code),
                exam_type: '',
                tree_name: '',
                edu_ver: '',
                updated_at: new Date().toISOString(),
                exam_count: 0,
                difficulty: '',
                length: 0,
                total_record: [],
                record_acc: [],
                accuracy: [],
                rating: 0,
                delta: 0
              });
            }
            
            // 동일한 tree_name의 exam들만 필터링
            const currentTreeName = currentExam?.tree_name || '';
            const sameTreeExams = allTests.filter(test => test.tree_name === currentTreeName);
            if (sameTreeExams.length > 0) {
              setExamList(sameTreeExams);
              // 현재 exam의 인덱스 찾기
              const currentIndex = sameTreeExams.findIndex(exam => exam.exam_id === parseInt(code));
              if (currentIndex !== -1) {
                setCurrentExamIndex(currentIndex);
              }
            } else {
              // tree_name이 같은 exam이 없는 경우 현재 exam만 표시
              setExamList(currentExam ? [currentExam] : []);
              setCurrentExamIndex(0);
            }
          }
          
          setIsLoading(false);
        }
      } catch (error) {
        console.error('데이터를 불러오는데 실패했습니다:', error);
        setIsLoading(false);
        if (error.response?.status === 401) {
          localStorage.clear();
          sessionStorage.clear(); // 캐시된 데이터도 함께 제거
          navigate('/', { replace: true });
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [code, navigate, location.state?.forceRefresh]);

  // Parse table data based on current dimension
  const getTableData = () => {
    if (!resultMapData || !resultMapData.result_map) {
      return [];
    }
    
    const dimensionKey = `d${currentDimension}_y_axis`;
    const axisData = resultMapData.result_map[dimensionKey];
    
    if (!axisData) return [];
    
    // 모든 회차의 정답/오답 누적 계산
    const accumulatedStats = {};
    // 현재 테스트의 출제 문제 정보 저장
    const currentExamProblems = {};
    
    // 현재 exam의 tree_name 찾기 (stamp_id보다 tree_name이 더 정확)
    const currentExamId = parseInt(code);
    const currentExam = testHistory.find(exam => exam.exam_id === currentExamId);
    const currentTreeName = currentExam?.tree_name;
    
    if (testHistory && testHistory.length > 0 && currentTreeName) {
      // 같은 tree_name의 exam들만 필터링하고 exam_id로 정렬
      const sameTreeExams = testHistory
        .filter(exam => exam.tree_name === currentTreeName)
        .sort((a, b) => a.exam_id - b.exam_id);
      
      
      // 현재 exam_id까지만 누적
      let processedCount = 0;
      sameTreeExams.forEach((exam) => {
        // 현재 exam_id보다 큰 것은 제외 (미래 데이터)
        if (exam.exam_id > currentExamId) {
          return;
        }
        
        processedCount++;
        
        if (exam.result && exam.problems_info) {
          Object.entries(exam.result).forEach(([problemId, isCorrect]) => {
            const problemInfo = exam.problems_info[problemId];
            if (problemInfo && problemInfo.address) {
              const addressParts = problemInfo.address.split('/');
              const level = problemInfo.step || 0;
              
              // 모든 dimension별로 키를 생성하여 저장
              // d1: 2개 숫자 (예: "16/3")
              const d1Key = addressParts.slice(0, 2).join('/');
              // d2: 3개 숫자 (예: "16/3/1")
              const d2Key = addressParts.slice(0, 3).join('/');
              // d3: 4개 숫자 (예: "16/3/1/3")
              const d3Key = addressParts.slice(0, 4).join('/');
              // d4: 5개 숫자 (예: "16/3/1/3/1")
              const d4Key = addressParts.slice(0, 5).join('/');
              
              // 각 dimension별로 데이터 저장
              [d1Key, d2Key, d3Key, d4Key].forEach((key, dimIndex) => {
                if (!accumulatedStats[key]) {
                  accumulatedStats[key] = {};
                }
                if (!accumulatedStats[key][level]) {
                  accumulatedStats[key][level] = { correct: 0, wrong: 0 };
                }
                
                if (isCorrect) {
                  accumulatedStats[key][level].correct++;
                } else {
                  accumulatedStats[key][level].wrong++;
                }
                
                // 현재 exam의 문제인 경우 문제 번호 저장
                if (exam.exam_id === currentExamId) {
                  // 현재 dimension에 해당하는 key만 저장
                  if (dimIndex + 1 === currentDimension) {
                    if (!currentExamProblems[key]) {
                      currentExamProblems[key] = {};
                    }
                    if (!currentExamProblems[key][level]) {
                      currentExamProblems[key][level] = [];
                    }
                    currentExamProblems[key][level].push(problemId);
                  }
                }
              });
              
            }
          });
        }
      });
      
    }
    
    const tableData = [];
    
    Object.entries(axisData).forEach(([axisKey, value]) => {
      if (Array.isArray(value) && value.length >= 2) {
        const categoryName = value[0];
        const levelData = value[1];
        
        const rowData = {
          name: categoryName,
          levels: {}
        };
        
        // levelData가 배열인 경우 (각 인덱스가 레벨 1-5에 대응)
        if (Array.isArray(levelData) && levelData.length === 5) {
          levelData.forEach((statusCode, idx) => {
            const level = (idx + 1).toString(); // 레벨 1-5
            
            // 상태 코드에 따른 색상 분류
            let cellStatus = 'empty';
            if (statusCode === 201 || statusCode === 101) {
              cellStatus = 'correct';
            } else if (statusCode === 301 || statusCode === 401 || statusCode === 501) {
              cellStatus = 'wrong';
            } else if (statusCode === 200) {
              cellStatus = 'partial';
            }
              
            // 누적된 정답/오답 수 가져오기
            let stats = { correct: 0, wrong: 0 };
            
            // accumulatedStats[axisKey]가 있고, 해당 레벨 데이터가 있는지 확인
            if (accumulatedStats[axisKey] && accumulatedStats[axisKey][level]) {
              stats = accumulatedStats[axisKey][level];
            }
            
            // 현재 테스트의 출제 문제 번호 추가
            const examProblems = currentExamProblems[axisKey] && currentExamProblems[axisKey][level] 
              ? currentExamProblems[axisKey][level] 
              : [];
            
            rowData.levels[level] = {
              status: cellStatus,
              problemNumbers: examProblems,
              count: 1,
              correctCount: stats.correct,
              wrongCount: stats.wrong
            };
          });
        }
        
        tableData.push(rowData);
      }
    });
    
    return tableData;
  };

  const dimensionLabels = {
    1: '대유형',
    2: '소단원', 
    3: '대유형',
    4: '중유형'
  };

  // 렌더링 직전에 상태 확인

  const handleShare = async () => {
    console.log('handleShare 함수 실행됨');
    try {
      const token = localStorage.getItem('access_token');
      console.log('토큰:', token);

      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/resultmap/issue`,
        { exam_id: parseInt(code) },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      console.log('API 응답:', response.data);

      if (response.data) {
        const url = `${window.location.origin}/result/${response.data.data}`;
        setShareUrl(url);
        setIsShareModalOpen(true);
      }
    } catch (error) {
      console.error('에러 발생:', error);
      setToastMessage('결과 공유 링크 생성에 실패했습니다.');
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
    }
  };

  const copyToClipboard = async (text) => {
    if (/^((?!chrome|android).)*safari/i.test(navigator.userAgent)) {
      // Safari 전용 복사 로직
      const tempInput = document.createElement('input');
      tempInput.value = text;
      tempInput.style.cssText = 'position: absolute; left: -9999px; top: -9999px';
      document.body.appendChild(tempInput);
      tempInput.focus();
      tempInput.setSelectionRange(0, 99999);

      try {
        const successful = document.execCommand('copy');
        if (successful) {
          setToastMessage('링크가 복사되었습니다.');
        } else {
          throw new Error('복사 실패');
        }
      } catch (err) {
        setToastMessage('복사에 실패했습니다. 링크를 직접 선택해주세요.');
      } finally {
        document.body.removeChild(tempInput);
      }
    } else {
      // 다른 브라우저용 복사 로직
      try {
        await navigator.clipboard.writeText(text);
        setToastMessage('링크가 복사되었습니다.');
      } catch (err) {
        setToastMessage('복사에 실패했습니다. 링크를 직접 선택해주세요.');
      }
    }
    setShowToast(true);
    setIsShareModalOpen(false);
    setTimeout(() => setShowToast(false), 3000);
  };

  const handleBack = () => {
    navigate('/kytest');
  };

  const handlePrevExam = () => {
    if (examList.length > 0 && currentExamIndex > 0) {
      const prevExamId = examList[currentExamIndex - 1].exam_id;
      navigate(`/myresult/${prevExamId}`);
    }
  };

  const handleNextExam = () => {
    if (examList.length > 0 && currentExamIndex < examList.length - 1) {
      const nextExamId = examList[currentExamIndex + 1].exam_id;
      navigate(`/myresult/${nextExamId}`);
    }
  };

  const formatExamDate = (dateString) => {
    const date = new Date(dateString);
    const year = date.getFullYear().toString().slice(2);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}.${month}.${day}`;
  };

  // PR 값을 가져오는 함수 수정
  const getPrValue = () => {
    return rating || "-";
  };

  // Delta 값을 가져오는 함수 추가
  const getDeltaValue = () => {
    return delta || "-";
  };

  // stampId 결정: 우선 examInfo.stamp_id, 없으면 URL 쿼리에서 추출
  const searchParams = new URLSearchParams(location.search);
  const stampIdFromQuery = searchParams.get('stampId');
  // examInfo에서 stamp_id를 찾거나, 없으면 testHistory에서 현재 exam_id와 일치하는 항목의 stamp_id 사용
  const currentExamFromHistory = testHistory.find(exam => exam.exam_id === parseInt(code));
  const effectiveStampId = examInfo?.stamp_id ?? currentExamFromHistory?.stamp_id ?? (stampIdFromQuery ? parseInt(stampIdFromQuery, 10) : undefined);

  // 문제 클릭 시 테이블 하이라이트 및 스크롤 이동 함수
  const handleProblemClick = (problem) => {
    console.log('문제 클릭됨:', problem);
    
    if (problem.address && problem.step) {
      // address를 현재 dimension에 맞게 파싱
      const addressParts = problem.address.split('/');
      let targetAddress = '';
      
      switch (currentDimension) {
        case 1: targetAddress = addressParts.slice(0, 2).join('/'); break;
        case 2: targetAddress = addressParts.slice(0, 3).join('/'); break;
        case 3: targetAddress = addressParts.slice(0, 4).join('/'); break;
        case 4: targetAddress = addressParts.slice(0, 5).join('/'); break;
        default: targetAddress = addressParts.slice(0, 5).join('/'); break;
      }
      
      console.log('파싱된 주소:', targetAddress, '레벨:', problem.step);
      
      // 하이라이트할 셀 정보 설정
      setHighlightedCell({
        address: targetAddress,
        level: problem.step.toString()
      });
      
      // 테이블로 스크롤 이동 (약간의 지연을 두어 렌더링 완료 후 스크롤)
      setTimeout(() => {
        scrollToTableCell(targetAddress, problem.step);
      }, 100);
    }
  };

  // 테이블 섹션으로 스크롤 이동 함수
  const scrollToTableCell = (targetAddress, targetLevel) => {
    // 먼저 테이블 섹션으로 스크롤
    if (tableScrollRef.current) {
      tableScrollRef.current.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      });
    }
    
    // 특정 셀이 있는 행을 찾아서 스크롤 (테이블 내부 스크롤)
    setTimeout(() => {
      const tableContainer = document.querySelector('.table-scroll-container');
      if (tableContainer && targetAddress && targetLevel) {
        // 테이블 데이터에서 해당 행 찾기
        const tableData = getTableData();
        const targetRowIndex = tableData.findIndex(row => {
          // 현재 dimension의 address key 찾기
          const dimensionKey = `d${currentDimension}_y_axis`;
          const axisData = resultMapData?.result_map?.[dimensionKey];
          if (!axisData) return false;
          
          // 해당 row의 address key 찾기
          const foundKey = Object.keys(axisData).find(key => {
            const data = axisData[key];
            return Array.isArray(data) && data.length >= 2 && data[0] === row.name;
          });
          
          return foundKey === targetAddress;
        });
        
        console.log('찾은 행 인덱스:', targetRowIndex, '전체 행 수:', tableData.length);
        
        if (targetRowIndex >= 0) {
          // 행의 높이를 40px로 가정하고 스크롤 위치 계산
          const rowHeight = 40;
          const targetScrollTop = targetRowIndex * rowHeight;
          const containerHeight = tableContainer.clientHeight;
          const scrollTop = Math.max(0, targetScrollTop - containerHeight / 2);
          
          tableContainer.scrollTo({
            top: scrollTop,
            behavior: 'smooth'
          });
        }
      }
    }, 300);
    
    // 하이라이트 효과는 10초 후 제거 (더 오래 유지)
    setTimeout(() => {
      setHighlightedCell(null);
    }, 10000);
  };

  if (isLoading) return (
    <div className="relative flex flex-col min-h-screen">
      <div className="fixed inset-0 bg-[#FAFAFA] z-0" />
      <div className="relative z-20 flex flex-col min-h-screen">
        <Header />
        <main className="flex-grow flex items-center justify-center">
          <div className="flex flex-col items-center">
            <div className="w-12 h-12 border-4 border-gray-500 border-t-transparent rounded-full animate-spin"></div>
            <p className="mt-4 text-gray-700 font-medium animate-pulse">로딩중...</p>
          </div>
        </main>
      </div>
    </div>
  );

  return (
    <div className="relative flex flex-col min-h-screen">
      <div className="fixed inset-0 bg-[#FAFAFA] z-0" />

      <div className="relative z-20 flex flex-col min-h-screen">
        <Header />
        <main className="flex-grow flex flex-col items-center pt-[84px] pb-[120px] px-4">
          <div className="max-w-[700px] w-full mx-auto">
            <div className="flex items-center justify-center mb-6 relative">
              <button
                onClick={handleBack}
                className="absolute left-0 flex items-center text-gray-600 hover:text-gray-800"
              >
                <ChevronLeft size={24} />
              </button>
              <h1 className="text-[18px] font-black text-[#4ABC85]">카이</h1>
              <h1 className="text-[18px] font-black text-[#231F20] pl-1">테스트</h1>
              {/* <h1 className="text-[18px] font-bold text-[#231F20]">카이테스트</h1> */}
              {/* <img src={logoKytest} alt="카이테스트" className="w-[93px] h-[20px]" /> */}
            </div>

            <div className="w-full flex flex-col gap-4">
              {examInfo ? (
                <TestCard
                  testData={{
                    ...examInfo,
                    tree_name: fieldInfo?.tree_name || '과목명',
                    edu_ver: fieldInfo?.edu_ver || '고등학교 1학년',
                    average_difficulty: examInfo.difficulty ? examInfo.difficulty / 100 : undefined,
                    problem_count: examInfo.length,
                    correct_count: examInfo.correct_count || 0,
                    wrong_count: examInfo.wrong_count || 0,
                    rating: rating || 0,
                    delta: delta || 0
                  }}
                  index={0}
                  isClickable={false}
                />
              ) : (
                <div className="w-full rounded-3xl p-4 shadow-sm" style={{ boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.08)' }}>
                  <div className="h-[72px] flex items-center justify-center">
                    <p className="text-gray-500">시험 정보가 없습니다</p>
                  </div>
                </div>
              )}

              {/* FormRatingTrendChart 추가 - 동일한 tree_name만 필터링 */}
              {testHistory.length > 0 && examInfo && (
                <div className="w-full">
                  <FormRatingTrendChart
                    selectedTest={examInfo}
                    testHistory={testHistory.filter(test => test.tree_name === examInfo.tree_name)}
                    customTitle="테스트별 변화 추이"
                    reportData={{
                      exams: testHistory
                        .filter(test => test.tree_name === examInfo.tree_name)
                        .map(e => ({ 
                          exam_id: e.exam_id, 
                          rating: e.rating ?? null, 
                          delta: e.delta ?? 0 
                        }))
                    }}
                    onTestSelect={(test) => {
                      if (test?.exam_id) {
                        navigate(`/myresult/${test.exam_id}`);
                      }
                    }}
                    hideTreeSelector={true}
                  />
                </div>
              )}

              {/* 난이도별 정답률 및 성취도 차트 섹션 */}
              {examInfo && (
                <DifficultyAnalysisPanel 
                  selectedTest={{
                    ...examInfo,
                    by_step: examInfo.by_step || (examInfo.record_acc && Array.isArray(examInfo.record_acc) && examInfo.record_acc.length >= 2 ? {
                      '1': [examInfo.record_acc[0][0] + examInfo.record_acc[1][0], examInfo.record_acc[0][0]],
                      '2': [examInfo.record_acc[0][1] + examInfo.record_acc[1][1], examInfo.record_acc[0][1]],
                      '3': [examInfo.record_acc[0][2] + examInfo.record_acc[1][2], examInfo.record_acc[0][2]],
                      '4': [examInfo.record_acc[0][3] + examInfo.record_acc[1][3], examInfo.record_acc[0][3]],
                      '5': [examInfo.record_acc[0][4] + examInfo.record_acc[1][4], examInfo.record_acc[0][4]]
                    } : null)
                  }}
                  testHistory={testHistory}
                />
              )}

              {/* 세부 유형별 문제 현황 섹션 */}
              {examInfo && (
                <div ref={tableScrollRef} className="rounded-3xl bg-white shadow-sm" style={{ boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.08)', height: '460px', display: 'flex', flexDirection: 'column' }}>
                  <div className="px-4 pt-4 flex-shrink-0">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-[14px] font-semibold">세부 유형별 문제 현황</h3>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-gray-700">출제문제</span>
                        <button
                          onClick={() => setShowExamOnly(!showExamOnly)}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none ${
                            showExamOnly 
                              ? 'bg-[#4ABC85]' 
                              : 'bg-gray-300'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform duration-200 ${
                              showExamOnly ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="flex-1 px-4 pb-4" style={{ minHeight: 0 }}>
                    <div className="h-full relative table-scroll-container" style={{ 
                      overflow: 'auto',
                      WebkitOverflowScrolling: 'touch',
                      scrollbarWidth: 'none',
                      msOverflowStyle: 'none'
                    }}>
                      <style>
                        {`
                          .table-scroll-container::-webkit-scrollbar {
                            display: none;
                          }
                          .table-scroll-container {
                            -webkit-overflow-scrolling: touch;
                            touch-action: pan-y;
                          }
                          .sticky-header {
                            position: sticky;
                            position: -webkit-sticky;
                            top: 0;
                            background-color: white;
                            z-index: 10;
                          }
                          .highlighted-cell {
                            animation: highlightPulse 1.5s ease-in-out infinite;
                          }
                          @keyframes highlightPulse {
                            0% {
                              box-shadow: 0 0 0 0 rgba(251, 191, 36, 0.8);
                              transform: scale(1.05);
                            }
                            50% {
                              box-shadow: 0 0 0 8px rgba(251, 191, 36, 0.4);
                              transform: scale(1.15);
                            }
                            100% {
                              box-shadow: 0 0 0 0 rgba(251, 191, 36, 0.8);
                              transform: scale(1.05);
                            }
                          }
                        `}
                      </style>
                      <table className="w-full text-xs table-fixed">
                        <thead className="sticky-header bg-white" style={{ zIndex: 10 }}>
                          <tr className="bg-white">
                            <th className="px-2 py-1 text-center w-[120px] bg-white">
                              <div className="flex items-center justify-between">
                                <button 
                                  className="w-5 h-5 flex items-center justify-center bg-[#7B7979] rounded-[8px] text-white hover:bg-[#666666] disabled:opacity-30"
                                  onClick={() => setCurrentDimension(prev => Math.max(1, prev - 1))}
                                  disabled={currentDimension === 1}
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="m15 18-6-6 6-6"></path>
                                  </svg>
                                </button>
                                <span className="text-[12px] text-[#231F20]">{dimensionLabels[currentDimension]}</span>
                                <button 
                                  className="w-5 h-5 flex items-center justify-center bg-[#7B7979] rounded-[8px] text-white hover:bg-[#666666] disabled:opacity-30"
                                  onClick={() => setCurrentDimension(prev => Math.min(4, prev + 1))}
                                  disabled={currentDimension === 4}
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="m9 18 6-6-6-6"></path>
                                  </svg>
                                </button>
                              </div>
                            </th>
                              {[1, 2, 3, 4, 5].map(level => (
                                <th key={level} className="!p-0 text-center !w-[20px] bg-white" style={{ width: '16px !important', minWidth: '16px !important', maxWidth: '28px !important' }}>
                                  <div className="flex flex-col items-center px-0.5 py-1">
                                    <span className="text-[8px] text-[#231F20]/60">Lv</span>
                                    <span className="text-[14px] text-[#231F20] font-medium">{level}</span>
                                  </div>
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {getTableData().length > 0 ? (
                              getTableData().filter(row => {
                                if (!showExamOnly) return true;
                                // 출제문제만 필터링: 적어도 하나의 레벨에 문제가 있는 행만 표시
                                return Object.values(row.levels).some(level => 
                                  level.problemNumbers && level.problemNumbers.length > 0
                                );
                              }).map((row, index) => (
                                <tr key={index} className="h-10">
                                  <td className="px-2 text-left align-middle">
                                    <span
                                      className="text-[12px] block truncate cursor-default"
                                      style={{ maxWidth: 'calc(100% - 8px)' }}
                                      title={row.name}
                                    >
                                      {row.name}
                                    </span>
                                  </td>
                                  {[1, 2, 3, 4, 5].map(level => {
                                    const levelData = row.levels[level] || { status: 'empty', problemNumbers: [], correctCount: 0, wrongCount: 0 };
                                    const hasBorder = levelData.problemNumbers && levelData.problemNumbers.length > 0;
                                    
                                    // 하이라이트 조건 확인 - 단순화된 로직
                                    // 현재 행의 이름과 매칭되는 address key를 직접 찾기
                                    let currentRowAddress = null;
                                    const dimensionKey = `d${currentDimension}_y_axis`;
                                    const axisData = resultMapData?.result_map?.[dimensionKey];
                                    
                                    if (axisData) {
                                      // 현재 row.name과 일치하는 address key 찾기
                                      Object.entries(axisData).forEach(([key, value]) => {
                                        if (Array.isArray(value) && value.length >= 2 && value[0] === row.name) {
                                          currentRowAddress = key;
                                        }
                                      });
                                    }
                                    
                                    const isHighlighted = highlightedCell && 
                                      highlightedCell.address === currentRowAddress && 
                                      highlightedCell.level === level.toString();
                                    
                                    // 디버깅 로그
                                    if (highlightedCell && currentRowAddress) {
                                      console.log('하이라이트 체크:', {
                                        rowName: row.name,
                                        currentRowAddress,
                                        highlightedAddress: highlightedCell.address,
                                        level: level.toString(),
                                        highlightedLevel: highlightedCell.level,
                                        isHighlighted
                                      });
                                    }
                                    
                                    // 실제 데이터 사용
                                    const testCorrect = levelData.correctCount || 0;
                                    const testWrong = levelData.wrongCount || 0;
                                    const total = testCorrect + testWrong;
                                    
                                    // 크기 계산 (0일 때 1rem, 10이상일 때 2rem, 그 사이는 비율)
                                    const minSize = 1; // rem
                                    const maxSize = 1.75; // rem (28px)
                                    const maxTotal = 10;
                                    const sizeRatio = Math.min(total / maxTotal, 1);
                                    const boxSize = minSize + (maxSize - minSize) * sizeRatio;
                                    const boxSizeRem = `${boxSize}rem`;
                                    
                                    // 색상 결정 로직
                                    let bgColor = '#E5E5E5'; // 연한 회색 (데이터 없음)
                                    let showSplit = false;
                                    let topHeight = 0;
                                    
                                    if (total > 0) {
                                      if (testWrong === 0) {
                                        // 모두 맞춤
                                        bgColor = '#4ABC85';
                                      } else if (testCorrect === 0) {
                                        // 모두 틀림
                                        bgColor = '#FF6B6B';
                                      } else {
                                        // 맞춤과 틀림이 모두 있음 - 분할 표시
                                        showSplit = true;
                                        topHeight = (testWrong / total) * 100;
                                      }
                                    }
                                    
                                    return (
                                      <td key={level} className="text-center align-middle" style={{ width: '40px', minWidth: '40px', maxWidth: '40px', padding: '4px', position: 'relative', zIndex: 1 }}>
                                        <div 
                                          className={`rounded-xl relative overflow-hidden flex items-center justify-center mx-auto transition-all duration-300 ${
                                            hasBorder 
                                              ? 'border-2 border-black' 
                                              : ''
                                          } ${
                                            isHighlighted 
                                              ? 'ring-4 ring-yellow-400 ring-opacity-90 shadow-2xl highlighted-cell' 
                                              : ''
                                          }`}
                                          style={{ 
                                            backgroundColor: showSplit ? '#4ABC85' : bgColor,
                                            width: boxSizeRem,
                                            height: boxSizeRem,
                                            zIndex: isHighlighted ? 10 : 1
                                          }}
                                        >
                                          {showSplit && (
                                            <div 
                                              className="absolute top-0 left-0 w-full"
                                              style={{ 
                                                backgroundColor: '#FF6B6B',
                                                height: `${topHeight}%`
                                              }}
                                            />
                                          )}
                                          {(testCorrect > 0 || testWrong > 0) ? (
                                            <div className="font-bold text-black relative z-10" style={{ fontSize: `${boxSize * 0.35}rem` }}>
                                              {testCorrect}/{testWrong}
                                            </div>
                                          ) : null}
                                        </div>
                                      </td>
                                    );
                                  })}
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan={6} className="text-center py-8 text-gray-400">
                                  데이터를 불러오는 중...
                                </td>
                              </tr>
                            )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* 문제 리스트 카드 */}
              {examInfo && (
                <div className="rounded-3xl bg-white shadow-sm" style={{ boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.08)', height: '460px', display: 'flex', flexDirection: 'column' }}>
                  <div className="px-4 pt-4">
                    <h3 className="text-[14px] font-semibold mb-3">문제 리스트</h3>
                  </div>
                  {/* 내부 스크롤 컨테이너: 고정 높이(460px)에서 헤더 제외 영역만 스크롤 */}
                  <div className="px-4 pb-4" style={{ flex: 1, minHeight: 0, overflow: 'auto', WebkitOverflowScrolling: 'touch' }} ref={problemListScrollRef}>
                    <ExamProblemList
                      examId={examInfo.exam_id}
                      stampId={effectiveStampId}
                      accessToken={token}
                      isDesktop={false}
                      onProblemClick={handleProblemClick}
                      onViewProblem={(problemIds, index, isCorrect) => {
                        // 문제 상세보기 기능 - 필요시 구현
                        console.log('문제 상세보기:', { problemIds, index, isCorrect });
                      }}
                    />
                  </div>
                </div>
              )}

              {/* 버튼 영역: flex 컨테이너 추가 */}
              <div className="flex gap-4">
                {/* "공유하기" 버튼 (나의 폼 위치로 이동, 크기 조정) */}
                <button
                  onClick={handleShare}
                  className="flex-1 h-[50px] bg-white text-[#231F20] rounded-2xl text-sm font-bold hover:bg-gray-50 border border-gray-200 flex items-center justify-center gap-1.5"
                >
                  <Share size={18} />
                  공유하기
                </button>

                {/* "AI 리포트" 버튼 */}
                <button
                    onClick={() => {
                      if (reportValid) {
                        navigate(`/myresult/${code}/report`);
                      } else {
                        setToastMessage('AI 리포트가 준비되지 않았습니다.');
                        setShowToast(true);
                        setTimeout(() => setShowToast(false), 3000);
                      }
                    }}
                    className={`flex-1 h-[50px] ${reportValid ? 'bg-[#4ABC85] hover:bg-[#3da575]' : 'bg-[#CCCCCC] cursor-not-allowed'} text-white rounded-2xl text-sm font-bold flex items-center justify-center gap-1.5`}
                  >
                    <BookCheck size={18} />
                    AI 리포트
                </button>
              </div>

            </div>

          </div>
        </main>

        {showToast && (
          <div className="fixed bottom-[100px] left-1/2 transform -translate-x-1/2 bg-[#231F20] text-white px-4 py-3 rounded-lg shadow-lg z-[9999]" style={{ zIndex: 9999 }}>
            <div className="flex items-center space-x-2">
              <span className="text-sm">{toastMessage}</span>
              <button
                onClick={() => setShowToast(false)}
                className="ml-2"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        )}

        {isShareModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-6 w-[90%] max-w-[384px]">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold">공유하기</h3>
                <button onClick={() => setIsShareModalOpen(false)}>
                  <X size={24} />
                </button>
              </div>
              <div className="bg-gray-100 p-4 rounded-lg mb-4">
                <p className="text-sm break-all select-all">{shareUrl}</p>
              </div>
              <div className="flex flex-col gap-2">
                <button
                  onClick={() => copyToClipboard(shareUrl)}
                  className="w-full bg-[#4ABC85] text-white py-3 rounded-xl font-bold"
                >
                  링크 복사하기
                </button>
                <button
                  onClick={() => setIsShareModalOpen(false)}
                  className="w-full bg-gray-200 text-gray-700 py-3 rounded-xl font-bold"
                >
                  닫기
                </button>
              </div>
            </div>
          </div>
        )}

        {examList.length > 0 && (
          <>
            {/* 기존 기능 유지: 클릭 시 동일 핸들러 호출, disable 상태 동일 */}
            <FloatingButton
              variant="prev"
              onClick={handleNextExam}
              disabled={currentExamIndex === examList.length - 1}
              title="다음 테스트"
            />
            <FloatingButton
              variant="next"
              onClick={handlePrevExam}
              disabled={currentExamIndex === 0}
              title="이전 테스트"
            />
          </>
        )}

      </div>
    </div>
  );
}

export default MyResultPage;